import { useState, useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Box, Typography, IconButton, Chip, TextField, InputAdornment, Avatar, CircularProgress, Collapse } from '@mui/material';
import { Refresh, Search, Add, KeyboardArrowRight, Assignment, CalendarToday, TrendingUp, ShoppingCart, Inventory2, ExpandMore, ExpandLess, TrackChanges } from '@mui/icons-material';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';

/* ── Tokens ── */
const C = {
  bg: '#f1f5f9',
  card: '#fff',
  border: '#e2e8f0',
  text: '#0f172a',
  sec: '#475569',
  muted: '#94a3b8',
  blue: '#1e40af',
  green: '#16a34a',
  amber: '#d97706',
  red: '#dc2626',
  cyan: '#0891b2',
  purple: '#7c3aed',
};

const fmt = n => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '-';
const today = () => new Date().toISOString().split('T')[0];

/* ── Custom Pie Label ── */
const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, percent }) => {
  if (percent < 0.05) return null;
  const RADIAN = Math.PI / 180;
  const r = outerRadius * 0.7;
  const x = cx + r * Math.cos(-midAngle * RADIAN);
  const y = cy + r * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" style={{ fontSize: '0.65rem', fontWeight: 700 }}>
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

/* ── Section Card wrapper ── */
const Section = ({ title, icon, count, color, onViewAll, children, bgColor }) => (
  <Box sx={{ bgcolor: bgColor || C.card, borderRadius: 2.5, p: 1.5, mb: 1.75, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={{ width: 26, height: 26, borderRadius: '7px', bgcolor: `${color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {icon}
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: C.text }}>{title}</Typography>
        {count !== undefined && (
          <Chip label={count} size="small"
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${color}15`, color, border: `1px solid ${color}30` }} />
        )}
      </Box>
      {onViewAll && (
        <Box onClick={onViewAll} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: C.blue }}>
          <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>View All</Typography>
          <KeyboardArrowRight sx={{ fontSize: 15 }} />
        </Box>
      )}
    </Box>
    {children}
  </Box>
);

/* ── Enquiry Row ── */
const EnqRow = ({ item, onTap }) => {
  const isSaled = item.IsSaled;
  const isConf = item.IsConfirmOrder;
  const sc = isSaled ? C.green : isConf ? C.amber : C.muted;
  const sl = isSaled ? 'Converted' : isConf ? 'Confirmed' : 'Pending';
  return (
    <Box onClick={() => onTap && onTap(item)} sx={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      py: 1, px: 0.5, borderBottom: `1px solid ${C.border}`, cursor: 'pointer',
      '&:last-child': { borderBottom: 'none' }, '&:active': { bgcolor: '#f8fafc' }
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: C.text }} noWrap>{item.CustomerName || '-'}</Typography>
        <Typography sx={{ fontSize: '0.65rem', color: C.muted }}>{item.EnquiryNo} · {fmtDate(item.EnquiryDate)}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.4, ml: 1 }}>
        <Chip label={sl} size="small"
          sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: `${sc}15`, color: sc, border: `1px solid ${sc}30` }} />
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: C.blue }}>{fmt(item.TotalAmount)}</Typography>
      </Box>
    </Box>
  );
};

/* ── Order Row ── */
const OrderRow = ({ item, onTap }) => (
  <Box onClick={() => onTap && onTap(item)} sx={{
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    py: 1, px: 0.5, borderBottom: `1px solid #fde68a`, cursor: 'pointer',
    '&:last-child': { borderBottom: 'none' }, '&:active': { bgcolor: '#fef9c3' }
  }}>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: C.text }} noWrap>{item.CustomerName || '-'}</Typography>
      <Typography sx={{ fontSize: '0.65rem', color: C.muted }}>{item.EnquiryNo} · {fmtDate(item.EnquiryDate)}</Typography>
      {item.ContactNumber && <Typography sx={{ fontSize: '0.62rem', color: C.muted }}>📞 {item.ContactNumber}</Typography>}
    </Box>
    <Box sx={{ textAlign: 'right', ml: 1 }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 800, color: C.amber }}>{fmt(item.TotalAmount)}</Typography>
      <Typography sx={{ fontSize: '0.6rem', color: C.muted }}>Order</Typography>
    </Box>
  </Box>
);

/* ── Stock Row — expandable with branch stock + MOP rates ── */
const StockRow = ({ item }) => {
  const [open, setOpen] = useState(false);
  const curStock = parseFloat(item.CurrentBranchStock ?? item.BalanceStock ?? 0);
  const isLow = curStock <= 5;

  // Branch-wise stock fields (keys as returned by SaleApi ItemStockListDetails)
  const branchStocks = [
    { label: 'CHR', val: item.CHR },
    { label: 'NPR', val: item.NPR },
    { label: 'GD01', val: item.GD01 },
    { label: 'GD02', val: item.GD02 },
    { label: 'GD03', val: item.GD03 },
  ].filter(b => b.val !== undefined && b.val !== null && parseFloat(b.val) >= 0);

  const mops = [
    { label: 'MRP', val: item.MRP },
    { label: 'MOP1', val: item.MOP1 },
    { label: 'MOP2', val: item.MOP2 },
    { label: 'MOP3', val: item.MOP3 },
    { label: 'MOP4', val: item.MOP4 },
    { label: 'MOP5', val: item.MOP5 },
    { label: 'Sale Rate', val: item.SaleRate },
  ].filter(m => m.val && parseFloat(m.val) > 0);

  return (
    <Box sx={{ borderBottom: `1px solid ${C.border}`, '&:last-child': { borderBottom: 'none' } }}>
      {/* Main row */}
      <Box onClick={() => setOpen(o => !o)} sx={{
        display: 'flex', alignItems: 'center', py: 1, px: 0.5, cursor: 'pointer',
        '&:active': { bgcolor: '#f8fafc' }
      }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: C.text }} noWrap>
            {item.Name || '-'}
          </Typography>
          <Typography sx={{ fontSize: '0.62rem', color: C.muted }}>
            {item.Code ? `${item.Code}` : ''}{item.HSNCode ? ` · HSN: ${item.HSNCode}` : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, ml: 1 }}>
          <Box sx={{ textAlign: 'right' }}>
            <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: isLow ? C.red : C.green, lineHeight: 1 }}>
              {curStock}
            </Typography>
            <Typography sx={{ fontSize: '0.58rem', color: C.muted }}>units</Typography>
          </Box>
          {open ? <ExpandLess sx={{ fontSize: 16, color: C.muted }} /> : <ExpandMore sx={{ fontSize: 16, color: C.muted }} />}
        </Box>
      </Box>

      {/* Expanded details */}
      <Collapse in={open}>
        <Box sx={{ px: 1, pb: 1.25, bgcolor: '#f8fafc', borderRadius: 1, mx: 0.5, mb: 0.5 }}>

          {/* Branch-wise stocks */}
          {branchStocks.length > 0 && (
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: C.sec, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5 }}>
                Branch Stock
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {branchStocks.map(b => {
                  const v = parseFloat(b.val);
                  const low = v <= 5;
                  return (
                    <Box key={b.label} sx={{
                      px: 0.75, py: 0.3, borderRadius: 1, bgcolor: low ? '#fef2f2' : '#f0fdf4',
                      border: `1px solid ${low ? '#fecaca' : '#bbf7d0'}`,
                      display: 'flex', alignItems: 'center', gap: 0.4
                    }}>
                      <Typography sx={{ fontSize: '0.62rem', fontWeight: 600, color: C.sec }}>{b.label}</Typography>
                      <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: low ? C.red : C.green }}>{v}</Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* MOP Rates */}
          {mops.length > 0 && (
            <Box>
              <Typography sx={{ fontSize: '0.6rem', fontWeight: 700, color: C.sec, textTransform: 'uppercase', letterSpacing: '0.4px', mb: 0.5 }}>
                Price List
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {mops.map(m => (
                  <Box key={m.label} sx={{
                    px: 0.75, py: 0.3, borderRadius: 1,
                    bgcolor: m.label === 'MRP' ? '#eff6ff' : '#fafafa',
                    border: `1px solid ${m.label === 'MRP' ? '#bfdbfe' : C.border}`,
                    display: 'flex', alignItems: 'center', gap: 0.4
                  }}>
                    <Typography sx={{ fontSize: '0.6rem', fontWeight: 600, color: m.label === 'MRP' ? C.blue : C.sec }}>{m.label}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 700, color: C.text }}>₹{parseFloat(m.val).toLocaleString('en-IN', { maximumFractionDigits: 2 })}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {branchStocks.length === 0 && mops.length === 0 && (
            <Typography sx={{ fontSize: '0.7rem', color: C.muted }}>No additional details</Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

/* ════════════════════════════════════════
   DASHBOARD
════════════════════════════════════════ */
export default function SalesExecutiveDashboard() {
  useSessionValidation();
  const history = useHistory();
  const { companyId, branchId, token, username, userId } = useAuth();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [enquiries, setEnquiries] = useState([]);
  const [stockItems, setStockItems] = useState([]);
  const [enqSearch, setEnqSearch] = useState('');
  const [stockSearch, setStockSearch] = useState('');
  const [enqTab, setEnqTab] = useState('all');

  // ── Target state ──
  const now = new Date();
  const [targetData, setTargetData] = useState(null); // single row or null
  const [targetMonth] = useState(now.getMonth() + 1);
  const [targetYear]  = useState(now.getFullYear());

  const load = useCallback(async () => {
    try {
      const [enqRes, stockRes, tgtRes] = await Promise.allSettled([
        fetchData('EnquiryApi', `/${companyId}/${branchId}/${userId}/2000-01-01/${today()}`, token),
        fetchData('SaleApi', `/ItemStockListDetails/ItemStockListDetails/${companyId}/${branchId}/${branchId}/0`, token),
        fetchData('ReportApi', `/StaffTargetAchievementByUserId/${companyId}/${branchId}/${targetMonth}/${targetYear}/${userId}`, token),
      ]);
      if (enqRes.status === 'fulfilled') {
        const d = enqRes.value?.response_data || enqRes.value || [];
        setEnquiries(Array.isArray(d) ? d : []);
      }
      if (stockRes.status === 'fulfilled') {
        const d = stockRes.value?.response_data || stockRes.value || [];
        setStockItems(Array.isArray(d) ? d : []);
      }
      if (tgtRes.status === 'fulfilled') {
        const d = tgtRes.value?.response_data || tgtRes.value;
        // API may return array or single object
        const row = Array.isArray(d) ? d[0] : d;
        setTargetData(row || null);
      }
    } catch (e) { console.error(e); }
  }, [companyId, branchId, userId, token, targetMonth, targetYear]);

  useEffect(() => {
    if (companyId && token) { setLoading(true); load().finally(() => setLoading(false)); }
  }, [companyId, token, load]);

  const refresh = async () => { setRefreshing(true); await load(); setRefreshing(false); };

  /* ── Derived ── */
  const pending = enquiries.filter(e => !e.IsConfirmOrder && !e.IsSaled);
  const confirmed = enquiries.filter(e => e.IsConfirmOrder && !e.IsSaled);
  const converted = enquiries.filter(e => e.IsSaled);

  const pieData = [
    { name: 'Pending', value: pending.length, color: C.muted },
    { name: 'Orders', value: confirmed.length, color: C.amber },
    { name: 'Converted', value: converted.length, color: C.green },
  ].filter(d => d.value > 0);

  const TABS = [
    { key: 'all', label: 'All', arr: enquiries },
    { key: 'pending', label: 'Pending', arr: pending },
    { key: 'confirmed', label: 'Orders', arr: confirmed },
    { key: 'converted', label: 'Converted', arr: converted },
  ];
  const activeArr = TABS.find(t => t.key === enqTab)?.arr || enquiries;
  const filteredEnq = activeArr.filter(e => {
    const s = enqSearch.toLowerCase();
    return !s || (e.CustomerName || '').toLowerCase().includes(s) || (e.EnquiryNo || '').includes(s);
  });

  const filteredStock = stockItems.filter(i => {
    const s = stockSearch.toLowerCase();
    return !s || (i.Name || '').toLowerCase().includes(s) || (i.Code || '').toLowerCase().includes(s);
  });

  if (loading) return <LogoLoader open={true} message="Loading..." />;

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', pb: 5 }}>

      {/* ── Header ── */}
      <Box sx={{
        background: 'linear-gradient(135deg, #0f2557 0%, #1e3a8a 50%, #1d4ed8 100%)',
        px: 2, pt: 2.5, pb: 4, position: 'relative', overflow: 'hidden',
      }}>
        {[{ t: -30, r: -20, s: 120 }, { t: 20, r: 60, s: 60 }, { b: -15, l: 30, s: 80 }].map((c, i) => (
          <Box key={i} sx={{ position: 'absolute', top: c.t, right: c.r, bottom: c.b, left: c.l, width: c.s, height: c.s, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.05)' }} />
        ))}

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Avatar sx={{ width: 40, height: 40, bgcolor: 'rgba(255,255,255,0.2)', fontWeight: 800, fontSize: '1rem' }}>
              {(username || 'E').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.65rem' }}>Sales Executive</Typography>
              <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.95rem' }}>{username || 'Executive'}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton size="small" onClick={() => history.push('/EnquiryForm/create')}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', width: 34, height: 34 }}>
              <Add sx={{ fontSize: 18 }} />
            </IconButton>
            <IconButton size="small" onClick={refresh} disabled={refreshing}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: '#fff', width: 34, height: 34 }}>
              {refreshing ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <Refresh sx={{ fontSize: 18 }} />}
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1.5, position: 'relative', zIndex: 1 }}>
          <CalendarToday sx={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }} />
          <Typography sx={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)' }}>
            {new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
          </Typography>
        </Box>

        {/* KPI strip */}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, position: 'relative', zIndex: 1 }}>
          {[
            { v: enquiries.length, l: 'Enquiries', color: '#93c5fd' },
            { v: confirmed.length, l: 'Orders', color: '#fbbf24' },
            { v: converted.length, l: 'Converted', color: '#4ade80' },
          ].map(k => (
            <Box key={k.l} sx={{ flex: 1, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, py: 1.25, textAlign: 'center' }}>
              <Typography sx={{ color: k.color, fontWeight: 800, fontSize: '1.35rem', lineHeight: 1 }}>{k.v}</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 0.3 }}>{k.l}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ px: 1.5, mt: -1.5 }}>

        {/* ── My Target Card ── */}
        {targetData && (() => {
          const pct      = parseFloat(targetData.AchievementPercentage) || 0;
          const tgt      = parseFloat(targetData.TargetAmount)   || 0;
          const ach      = parseFloat(targetData.AchievedAmount) || 0;
          const bal      = parseFloat(targetData.BalanceAmount)  || 0;
          const barPct   = Math.min(pct, 100);
          const bc       = pct >= 100 ? C.green : pct >= 75 ? C.amber : C.red;
          const sl       = pct >= 100 ? '🏆 Achieved' : pct >= 75 ? '📈 On Track' : '⚠ Below';
          const MONTHS   = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
          const fmtC     = n => `₹${(parseFloat(n)||0).toLocaleString('en-IN',{maximumFractionDigits:0})}`;
          return (
            <Box sx={{
              bgcolor: C.card, borderRadius: 2, p: '10px 12px', mb: 1.5,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              borderLeft: `3px solid ${bc}`, border: `1px solid ${bc}30`,
            }}>
              {/* Row 1 — title + status + % */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                  <TrackChanges sx={{ fontSize: 14, color: bc }} />
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: C.text }}>My Target</Typography>
                  <Typography sx={{ fontSize: '0.6rem', color: C.muted }}>{MONTHS[targetMonth]} {targetYear}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 800, color: bc }}>{pct.toFixed(1)}%</Typography>
                  <Chip label={sl} size="small"
                    sx={{ height: 16, fontSize: '0.58rem', fontWeight: 700, bgcolor: `${bc}12`, color: bc, border: `1px solid ${bc}30` }} />
                </Box>
              </Box>

              {/* Row 2 — 3 metrics inline */}
              <Box sx={{ display: 'flex', gap: 0.75, mb: 0.75 }}>
                {[
                  { l: 'Target',   v: fmtC(tgt), c: C.blue  },
                  { l: 'Achieved', v: fmtC(ach), c: C.green },
                  { l: 'Balance',  v: fmtC(bal), c: C.red   },
                ].map(m => (
                  <Box key={m.l} sx={{ flex: 1, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 1, py: '5px', border: `1px solid ${C.border}` }}>
                    <Typography sx={{ fontSize: '0.55rem', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{m.l}</Typography>
                    <Typography sx={{ fontSize: '0.68rem', fontWeight: 800, color: m.c }}>{m.v}</Typography>
                  </Box>
                ))}
              </Box>

              {/* Row 3 — progress bar only */}
              <Box sx={{ height: 5, borderRadius: 3, bgcolor: `${bc}18`, overflow: 'hidden' }}>
                <Box sx={{
                  height: '100%', borderRadius: 3, width: `${barPct}%`,
                  background: pct >= 100 ? `linear-gradient(90deg,${C.green},#4ade80)` : pct >= 75 ? `linear-gradient(90deg,${C.amber},#fbbf24)` : `linear-gradient(90deg,${C.red},#f87171)`,
                  transition: 'width 0.5s ease',
                }} />
              </Box>
            </Box>
          );
        })()}

        {/* ── Pie Chart Card ── */}
        {pieData.length > 0 && (
          <Box sx={{
            bgcolor: C.card, borderRadius: 2.5, p: 1.5, mb: 1.75,
            boxShadow: '0 2px 8px rgba(0,0,0,0.09)', border: `1px solid ${C.border}`,
            mt: 0
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <TrendingUp sx={{ fontSize: 16, color: C.blue }} />
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: C.text }}>Enquiry Overview</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* Pie */}
              <Box sx={{ width: 130, height: 130, flexShrink: 0 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={pieData} cx="50%" cy="50%" innerRadius={32} outerRadius={58}
                      dataKey="value" labelLine={false} label={renderCustomLabel} paddingAngle={2}>
                      {pieData.map((entry) => <Cell key={entry.name} fill={entry.color} />)}
                    </Pie>
                    <Tooltip formatter={(v, n) => [v, n]} contentStyle={{ fontSize: '0.72rem', borderRadius: 8, border: `1px solid ${C.border}` }} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              {/* Legend */}
              <Box sx={{ flex: 1 }}>
                {pieData.map(d => (
                  <Box key={d.name} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: '3px', bgcolor: d.color, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.75rem', color: C.sec }}>{d.name}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Typography sx={{ fontSize: '0.8rem', fontWeight: 700, color: d.color }}>{d.value}</Typography>
                      <Typography sx={{ fontSize: '0.62rem', color: C.muted }}>
                        ({enquiries.length > 0 ? Math.round((d.value / enquiries.length) * 100) : 0}%)
                      </Typography>
                    </Box>
                  </Box>
                ))}
                <Divider sx={{ my: 0.75 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: '0.68rem', color: C.muted }}>Total Value</Typography>
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: C.blue }}>
                    {fmt(enquiries.reduce((s, e) => s + (parseFloat(e.TotalAmount) || 0), 0))}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        )}

        {/* ── Enquiry List ── */}
        <Section title="Enquiries" icon={<Assignment sx={{ fontSize: 14, color: C.blue }} />}
          count={filteredEnq.length} color={C.blue} onViewAll={() => history.push('/EnquiryIndex')}>

          {/* Tabs */}
          <Box sx={{ display: 'flex', gap: 0.6, mb: 1.25, overflowX: 'auto', pb: 0.25 }}>
            {TABS.map(t => (
              <Box key={t.key} onClick={() => setEnqTab(t.key)} sx={{
                px: 1.1, py: 0.35, borderRadius: 10, cursor: 'pointer', flexShrink: 0,
                bgcolor: enqTab === t.key ? `${C.blue}12` : '#f8fafc',
                border: `1px solid ${enqTab === t.key ? C.blue : C.border}`,
              }}>
                <Typography sx={{ fontSize: '0.67rem', fontWeight: enqTab === t.key ? 700 : 500, color: enqTab === t.key ? C.blue : C.sec }}>
                  {t.label} {t.arr.length}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Search */}
          <TextField fullWidth size="small" placeholder="Search customer, enquiry no..." value={enqSearch}
            onChange={e => setEnqSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: C.muted }} /></InputAdornment> }}
            sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.78rem', bgcolor: '#f8fafc', '& fieldset': { borderColor: C.border } } }} />

          <Box sx={{ maxHeight: 340, overflowY: 'auto' }}>
            {filteredEnq.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ fontSize: '0.8rem', color: C.muted }}>No enquiries found</Typography>
              </Box>
            ) : filteredEnq.slice(0, 25).map(item => (
              <EnqRow key={item.Id} item={item} onTap={() => history.push(`/EnquiryForm/view/${item.Id}`)} />
            ))}
          </Box>
        </Section>

        {/* ── Placed Orders ── */}
        <Box sx={{
          bgcolor: '#fffbeb', borderRadius: 2.5, p: 1.5, mb: 1.75,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: `1px solid #fde68a`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 26, height: 26, borderRadius: '7px', bgcolor: `${C.amber}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <ShoppingCart sx={{ fontSize: 14, color: C.amber }} />
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: C.text }}>Placed Orders</Typography>
              <Chip label={confirmed.length} size="small"
                sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: `${C.amber}20`, color: C.amber, border: `1px solid ${C.amber}40` }} />
            </Box>
            <Box onClick={() => history.push('/EnquiryIndex')} sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', color: C.amber }}>
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 600 }}>View All</Typography>
              <KeyboardArrowRight sx={{ fontSize: 15 }} />
            </Box>
          </Box>

          {confirmed.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Typography sx={{ fontSize: '1.2rem' }}>📋</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: C.muted, mt: 0.5 }}>No pending orders</Typography>
            </Box>
          ) : confirmed.slice(0, 10).map(item => (
            <OrderRow key={item.Id} item={item} onTap={() => history.push(`/EnquiryForm/view/${item.Id}`)} />
          ))}
          {confirmed.length > 10 && (
            <Typography onClick={() => history.push('/EnquiryIndex')}
              sx={{ textAlign: 'center', fontSize: '0.7rem', color: C.amber, pt: 0.75, cursor: 'pointer', fontWeight: 600 }}>
              +{confirmed.length - 10} more orders
            </Typography>
          )}
        </Box>

        {/* ── Item Stock ── */}
        <Section title="Item Stock" icon={<Inventory2 sx={{ fontSize: 14, color: C.cyan }} />}
          count={filteredStock.length} color={C.cyan}>

          <TextField fullWidth size="small" placeholder="Search item..." value={stockSearch}
            onChange={e => setStockSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: C.muted }} /></InputAdornment> }}
            sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.78rem', bgcolor: '#f8fafc', '& fieldset': { borderColor: C.border } } }} />

          <Box sx={{ maxHeight: 380, overflowY: 'auto' }}>
            {filteredStock.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ fontSize: '0.8rem', color: C.muted }}>No items found</Typography>
              </Box>
            ) : filteredStock.slice(0, 50).map((item, i) => (
              <StockRow key={item.Id || i} item={item} />
            ))}
          </Box>
        </Section>

      </Box>
    </Box>
  );
}

/* Inline divider */
const Divider = ({ sx }) => <Box sx={{ height: 1, bgcolor: C.border, ...sx }} />;
